import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook to track and warn about unsaved changes
 *
 * Note: This uses a custom blocking mechanism since useBlocker requires
 * a data router (createBrowserRouter) and this app uses BrowserRouter.
 *
 * @param {boolean} hasUnsavedChanges - Whether there are unsaved changes
 * @param {string} message - Optional custom warning message
 * @returns {Object} - { showPrompt, confirmNavigation, cancelNavigation }
 */
const useUnsavedChanges = (hasUnsavedChanges, message = 'You have unsaved changes. Are you sure you want to leave?') => {
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  const navigate = useNavigate();
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Keep ref in sync with prop
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Handle browser beforeunload event (refresh, close tab, etc.)
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChangesRef.current) {
        event.preventDefault();
        // Modern browsers ignore custom messages and show their own
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [message]);

  // Intercept navigation attempts
  useEffect(() => {
    const handleClick = (event) => {
      if (!hasUnsavedChangesRef.current) return;

      // Check if this is a navigation link
      const link = event.target.closest('a[href]');
      if (link && link.href.startsWith(window.location.origin)) {
        const targetPath = new URL(link.href).pathname;

        // Only block if navigating to a different page
        if (targetPath !== location.pathname) {
          event.preventDefault();
          setPendingNavigation(targetPath);
          setShowPrompt(true);
        }
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [location.pathname]);

  // Confirm navigation
  const confirmNavigation = useCallback(() => {
    if (pendingNavigation) {
      setShowPrompt(false);
      // Temporarily disable the warning
      hasUnsavedChangesRef.current = false;
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [pendingNavigation, navigate]);

  // Cancel navigation
  const cancelNavigation = useCallback(() => {
    setShowPrompt(false);
    setPendingNavigation(null);
  }, []);

  return {
    showPrompt,
    confirmNavigation,
    cancelNavigation,
  };
};

export default useUnsavedChanges;
